import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "src/http/api-client/use-api-client";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";

/** Returns true if the user has opened the StubHub native app within 90 days. Fail-open (false) on error. */
export const fetchHasRecentNativeAppSession = async (
  apiClient: ReturnType<typeof useApiClient>,
  signal?: AbortSignal,
): Promise<boolean> => {
  try {
    const { data } = await apiClient.GET("/site/api/v1/NativeAppSession/last-activity", { signal: signal ?? null });
    return data === true;
  } catch {
    return false;
  }
};

/** React Query hook wrapping fetchHasRecentNativeAppSession. Cached for the page lifetime. */
export const useHasRecentNativeAppSession = (enabled = true) => {
  const apiClient = useApiClient();

  const { data, isLoading } = useQuery<boolean>({
    queryKey: createQueryKey(QueryKeyPrefix.NATIVE_APP_SESSION),
    queryFn: ({ signal }) => fetchHasRecentNativeAppSession(apiClient, signal),
    enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    hasRecentNativeAppSession: data ?? false,
    isLoading: enabled && isLoading,
  };
};
